import type { DifficultyLevel, TabSong, UserRole } from '../types/index.js';
import { toPersianDigits } from './timeUtils.js';

/** FRE-188: tab difficulty levels (spec 5.2). `null` = unrated («نامشخص»). */
export type DifficultyKind = 'rhythm' | 'lead';

export interface DifficultyLevelInfo {
  value: DifficultyLevel;
  label: string;
  rhythmHint: string;
  leadHint: string;
}

export const DIFFICULTY_LEVELS: readonly DifficultyLevelInfo[] = [
  { value: 1, label: 'مبتدی', rhythmHint: 'آکوردهای باز، ریتم ساده', leadHint: 'ملودی تک‌نت آهسته' },
  { value: 2, label: 'مقدماتی', rhythmHint: 'پاورکورد، تعویض ساده آکورد', leadHint: 'هامر، پول و اسلاید ساده' },
  { value: 3, label: 'متوسط', rhythmHint: 'بارِ کورد، ریتم سینکوپ', leadHint: 'سولوی پنتاتونیک، بند و ویبراتو' },
  { value: 4, label: 'پیشرفته', rhythmHint: 'ریف سریع، پالم‌میوت دقیق', leadHint: 'سرعت بالا، سوییپ و تپینگ ساده' },
  { value: 5, label: 'حرفه‌ای', rhythmHint: 'ریتم بسیار سریع یا نامنظم', leadHint: 'تکنیک‌های پیشرفته در سرعت بالا' }
];

export const DIFFICULTY_UNRATED_LABEL = 'نامشخص';
export const DIFFICULTY_KIND_LABEL: Record<DifficultyKind, string> = { rhythm: 'ریتم', lead: 'لید' };
export const DIFFICULTY_KIND_LONG_LABEL: Record<DifficultyKind, string> = {
  rhythm: 'سختی ریتم',
  lead: 'سختی لید'
};

export const DIFFICULTY_COPY = {
  title: 'سطح سختی',
  saved: 'سطح سختی ذخیره شد.',
  savedOffline: 'سطح سختی ذخیره شد و پس از اتصال همگام می‌شود.',
  rejected: 'تغییر سطح سختی پذیرفته نشد.',
  adminOnly: 'سطح سختی تب‌های منتشرشده را مدیر تعیین می‌کند.',
  offline: 'دستگاه در حالت آفلاین است.'
} as const;

export const isDifficultyLevel = (v: unknown): v is DifficultyLevel =>
  v === 1 || v === 2 || v === 3 || v === 4 || v === 5;

/** Narrows an untrusted value to a level, anything else becomes null (unrated). */
export const toDifficultyLevel = (v: unknown): DifficultyLevel | null => (isDifficultyLevel(v) ? v : null);

export const difficultyLevelInfo = (value: DifficultyLevel | null | undefined): DifficultyLevelInfo | null =>
  isDifficultyLevel(value) ? DIFFICULTY_LEVELS[value - 1] : null;

export const difficultyHint = (kind: DifficultyKind, value: DifficultyLevel | null | undefined): string => {
  const info = difficultyLevelInfo(value);
  return info ? (kind === 'rhythm' ? info.rhythmHint : info.leadHint) : '';
};

/** «سختی ریتم: ۳ از ۵، متوسط» or «سختی ریتم: نامشخص». */
export const difficultyAria = (kind: DifficultyKind, value: DifficultyLevel | null | undefined): string => {
  const info = difficultyLevelInfo(value);
  const head = DIFFICULTY_KIND_LONG_LABEL[kind];
  return info ? `${head}: ${toPersianDigits(info.value)} از ۵، ${info.label}` : `${head}: ${DIFFICULTY_UNRATED_LABEL}`;
};

/** A tab with no server row yet: a queued upload, or a purely local import (no file URL, no uploader). */
export const isLocalOnlyTab = (tab: Pick<TabSong, 'pendingUpload' | 'fileUrl' | 'uploaderId'>): boolean =>
  tab.pendingUpload === 1 || (!tab.fileUrl && !tab.uploaderId);

export interface DifficultyViewer {
  id?: string;
  role?: UserRole;
  isGuest: boolean;
}

export type DifficultyEditReason = 'local' | 'owner' | 'admin' | 'approved-owner' | 'none';

/** AC-59 permission matrix. */
export function canEditDifficulty(
  tab: Pick<TabSong, 'pendingUpload' | 'fileUrl' | 'uploaderId' | 'status'>,
  viewer: DifficultyViewer
): { editable: boolean; reason: DifficultyEditReason } {
  if (isLocalOnlyTab(tab)) return { editable: true, reason: 'local' };
  const signedIn = !viewer.isGuest && !!viewer.id;
  const isOwner = signedIn && !!tab.uploaderId && tab.uploaderId === viewer.id;
  if (isOwner && (tab.status === 'PRIVATE' || tab.status === 'PENDING' || tab.status === 'REJECTED')) {
    return { editable: true, reason: 'owner' };
  }
  if (signedIn && viewer.role === 'ADMIN' && (tab.status === 'PENDING' || tab.status === 'APPROVED' || tab.status === 'REJECTED')) {
    return { editable: true, reason: 'admin' };
  }
  if (isOwner && tab.status === 'APPROVED') return { editable: false, reason: 'approved-owner' };
  return { editable: false, reason: 'none' };
}
